import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { formatDateWithMinute } from 'src/utils/string/stringUtil';

import { PRD5011_R01Dto } from './Dto_PRD5011_R01';
import { usePRD5011_R01Store } from './Store_PRD5011_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'PRD5011_R01-grid-column-state';

const getKpiYnByTransdt = (transdt: string | null | undefined) => {
	if (transdt == null) return 'N';
	return 'Y';
};

interface Props {
	gridRef: React.RefObject<AgGridReact<PRD5011_R01Dto> | null>;
	defaultPRD5011_R01Row: PRD5011_R01Dto;
	fetchProductResultByRow: (row: PRD5011_R01Dto) => Promise<void>;
	clearProductResultData: () => void;
}

export const Table_PRD5011_R01 = (props: Props) => {
	const { gridRef, defaultPRD5011_R01Row, fetchProductResultByRow, clearProductResultData } = props;

	const {
		PRD5011_R01List,
		selectedPRD5011_R01,
		setSelectedPRD5011_R01,
		initSelectedPRD5011_R01,
		columnState,
		setColumnState,
		scrollTop,
		setScrollTop,
		setDetailScrollTop,
	} = usePRD5011_R01Store();

	const { compCdList } = useCommonCodeStore();
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const codeMaps = useMemo(() => ({
		comp: new Map(compCdList.map(item => [item.compCd, item.compNm])),
	}), [compCdList]);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'rowKey',
		gridData: PRD5011_R01List,
		defaultGridData: defaultPRD5011_R01Row,
		selectedRow: selectedPRD5011_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		rowKey: 'rowKey',
		compCd: '사업장',
		compNm: '사업장명',
		ocrdttm: '전송일자',
		systmopryn: '시스템여부',
		kpifldcd: 'KPI코드',
		kpidtlnm: '상세코드명',
		kpidtlcd: '상세코드',
		msmtvl: '실적값',
		unt: '데이터명',
		achrt: '달성률',
		kpiYn: '전송여부',
		transdt: '전송일시',
		remark: '비고',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'compCd')(params), []);

	const baseColumnDefs: ColDef<PRD5011_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '사업장', colId: 'compCd', field: 'compCd', width: 80,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'compCd'),
		},
		{
			headerName: '사업장명', colId: 'compNm', field: 'compNm', width: 180,
			headerClass: aggridHeaderClass,
			valueGetter: (params) => codeMaps.comp.get(params.data?.compCd ?? '') || params.data?.compNm || '',
			cellStyle: (p) => getCellStyle(p, 'compNm'),
		},
		{
			headerName: '전송일자', colId: 'ocrdttm', field: 'ocrdttm', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'ocrdttm'),
		},
		{
			headerName: '시스템여부', colId: 'systmopryn', field: 'systmopryn', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'systmopryn'),
		},
		{
			headerName: 'KPI코드', colId: 'kpifldcd', field: 'kpifldcd', width: 80,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'kpifldcd'),
		},
		{
			headerName: '상세코드명', colId: 'kpidtlnm', field: 'kpidtlnm', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'kpidtlnm'),
		},
		{
			headerName: '상세코드', colId: 'kpidtlcd', field: 'kpidtlcd', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'kpidtlcd'),
		},
		{
			headerName: '실적값', colId: 'msmtvl', field: 'msmtvl', width: 100,
			type: 'numericColumn',
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'msmtvl'),
		},
		{
			headerName: '단위', colId: 'unt', field: 'unt', width: 60,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'unt'),
		},
		{
			headerName: '달성율', colId: 'achrt', field: 'achrt', width: 100,
			type: 'numericColumn',
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'achrt'),
		},
		{
			headerName: '전송여부', colId: 'kpiYn', field: 'kpiYn', width: 80,
			headerClass: aggridHeaderClass,
			valueGetter: (params) => getKpiYnByTransdt(params.data?.transdt),
			cellStyle: (p) => getCellStyle(p, 'kpiYn'),
		},
		{
			headerName: '전송일시', colId: 'transdt', field: 'transdt', width: 120,
			headerClass: aggridHeaderClass,
			valueFormatter: (p: { data?: PRD5011_R01Dto }) =>
				p.data?.transdt ? formatDateWithMinute(new Date(p.data.transdt)) : '',
			cellStyle: (p) => getCellStyle(p, 'transdt'),
		},
	], [getCellStyle, codeMaps.comp]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<PRD5011_R01Dto>[] = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<PRD5011_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const handleRowClick = useCallback((e: RowClickedEvent<PRD5011_R01Dto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		const isSelected = selectedPRD5011_R01?.rowKey === rowData.rowKey;
		if (isSelected) {
			setSelectedPRD5011_R01(undefined);
			initSelectedPRD5011_R01();
			clearProductResultData();
		} else {
			setSelectedPRD5011_R01(rowData);
			setDetailScrollTop(null);
			fetchProductResultByRow(rowData);
		}
	}, [
		selectedPRD5011_R01,
		setSelectedPRD5011_R01,
		initSelectedPRD5011_R01,
		clearProductResultData,
		fetchProductResultByRow,
		setDetailScrollTop,
	]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		compCd: `${PRD5011_R01List.length.toLocaleString()}건`,
		editable: false,
	}]), [PRD5011_R01List]);

	useEffect(() => { ensureSelectedRow(); }, [PRD5011_R01List, selectedPRD5011_R01]);

	return (
		<GridContextWrapper<PRD5011_R01Dto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedPRD5011_R01}
			defaultRowData={defaultPRD5011_R01Row}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<PRD5011_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<PRD5011_R01Dto>({
					idKey: 'rowKey',
					selectedList: selectedPRD5011_R01 ? [selectedPRD5011_R01.rowKey] : [],
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
